import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ChatMessage, ChatbotVisualState, INITIAL_CHATBOT_MESSAGES } from '../../00_modules/ai-chatbot/models/ai-chatbot.model';
import {
    AppendAiChatbotMessage,
    ResetAiChatbotState,
    SetAiChatbotOrderContext,
    SetAiChatbotOrderContextConsent,
    SetAiChatbotOrderContextPrompted,
    SetAiChatbotOpen,
    SetAiChatbotThreadId,
    SetAiChatbotVisualState,
} from '../action/ai-chatbot.action';
import { ResetUserState } from '../action/user.action';

export interface AiChatbotStateModel {
    isOpen: boolean;
    threadId: string | null;
    visualState: ChatbotVisualState;
    messages: ChatMessage[];
    orderContextOrderId: number | null;
    orderContextConsent: boolean | null;
    orderContextPromptedOrderId: number | null;
}

const initialState: AiChatbotStateModel = {
    isOpen: false,
    threadId: null,
    visualState: 'initial',
    messages: [...INITIAL_CHATBOT_MESSAGES],
    orderContextOrderId: null,
    orderContextConsent: null,
    orderContextPromptedOrderId: null,
};

@State<AiChatbotStateModel>({
    name: 'aiChatbot',
    defaults: initialState,
})
@Injectable()
export class AiChatbotState {
    @Selector()
    static getState(state: AiChatbotStateModel): AiChatbotStateModel {
        return state;
    }

    @Selector()
    static getIsOpen(state: AiChatbotStateModel): boolean {
        return state.isOpen;
    }

    @Selector()
    static getThreadId(state: AiChatbotStateModel): string | null {
        return state.threadId;
    }

    @Selector()
    static getVisualState(state: AiChatbotStateModel): ChatbotVisualState {
        return state.visualState;
    }

    @Selector()
    static getMessages(state: AiChatbotStateModel): ChatMessage[] {
        return state.messages;
    }

    @Selector()
    static getOrderContextOrderId(state: AiChatbotStateModel): number | null {
        return state.orderContextOrderId;
    }

    @Selector()
    static getOrderContextConsent(state: AiChatbotStateModel): boolean | null {
        return state.orderContextConsent;
    }

    @Selector()
    static getOrderContextPromptedOrderId(state: AiChatbotStateModel): number | null {
        return state.orderContextPromptedOrderId;
    }

    @Action(SetAiChatbotOpen)
    setOpen(ctx: StateContext<AiChatbotStateModel>, action: SetAiChatbotOpen) {
        ctx.patchState({ isOpen: action.isOpen });
    }

    @Action(SetAiChatbotThreadId)
    setThreadId(ctx: StateContext<AiChatbotStateModel>, action: SetAiChatbotThreadId) {
        ctx.patchState({ threadId: action.threadId });
    }

    @Action(SetAiChatbotVisualState)
    setVisualState(ctx: StateContext<AiChatbotStateModel>, action: SetAiChatbotVisualState) {
        ctx.patchState({ visualState: action.visualState });
    }

    @Action(SetAiChatbotOrderContext)
    setOrderContext(ctx: StateContext<AiChatbotStateModel>, action: SetAiChatbotOrderContext) {
        const state = ctx.getState();
        if (state.orderContextOrderId === action.orderId) {
            return;
        }

        if (state.orderContextConsent !== null) {
            ctx.patchState({
                orderContextOrderId: action.orderId,
            });
            return;
        }

        ctx.patchState({
            orderContextOrderId: action.orderId,
            orderContextConsent: null,
            orderContextPromptedOrderId: null,
        });
    }

    @Action(SetAiChatbotOrderContextConsent)
    setOrderContextConsent(ctx: StateContext<AiChatbotStateModel>, action: SetAiChatbotOrderContextConsent) {
        if (ctx.getState().orderContextConsent !== null) {
            return;
        }

        ctx.patchState({ orderContextConsent: action.consent });
    }

    @Action(SetAiChatbotOrderContextPrompted)
    setOrderContextPrompted(ctx: StateContext<AiChatbotStateModel>, action: SetAiChatbotOrderContextPrompted) {
        const state = ctx.getState();
        if (state.orderContextPromptedOrderId === action.orderId) {
            return;
        }

        ctx.patchState({ orderContextPromptedOrderId: action.orderId });
    }

    @Action(AppendAiChatbotMessage)
    appendMessage(ctx: StateContext<AiChatbotStateModel>, action: AppendAiChatbotMessage) {
        const state = ctx.getState();
        ctx.patchState({ messages: [...state.messages, action.message] });
    }

    @Action([ResetAiChatbotState, ResetUserState])
    resetState(ctx: StateContext<AiChatbotStateModel>) {
        ctx.setState({
            ...initialState,
            messages: [...INITIAL_CHATBOT_MESSAGES],
        });
    }
}
